<template>
    <div class="admin-container">
        <div class="f sb flex-start pb-4 px-3">
            <h4 class="text-white">Giveaway Management</h4>
        </div>

        <!-- Simple Tab Navigation -->
        <div class="tab-nav">
            <button 
                :class="['tab-btn', { active: activeTab === 'giveaways' }]"
                @click="activeTab = 'giveaways'"
            >
                Giveaways
            </button>
            <button 
                :class="['tab-btn', { active: activeTab === 'tasks' }]"
                @click="activeTab = 'tasks'"
            >
                Tasks
            </button>
        </div>

        <!-- Tab Content -->
        <div class="tab-content">
            <!-- Giveaways Tab -->
            <div v-if="activeTab === 'giveaways'">
                <div class="f sb flex-start pb-4">
                    <button class="btn-pinko btn-pink" @click="showCreateModal = true">
                        Create New Giveaway
                    </button>
                </div>
                <!-- Filters -->
                <div class="filters-container px-3 mb-4">
                    <div class="filter-group">
                        <label>Type</label>
                        <select v-model="filters.type" class="form-control">
                            <option value="">All Types</option>
                            <option v-for="type in types" :key="type" :value="type">
                                {{ type }}
                            </option>
                        </select>
                    </div>
                    <div class="filter-group">
                        <label>Status</label>
                        <select v-model="filters.status" class="form-control">
                            <option value="">All Statuses</option>
                            <option v-for="status in statuses" :key="status" :value="status">
                                {{ status }}
                            </option>
                        </select>
                    </div>
                    <div class="filter-group">
                        <label>Giveaway Type</label>
                        <select v-model="filters.giveaway_type" class="form-control">
                            <option value="">All Giveaway Types</option>
                            <option v-for="type in giveawayTypes" :key="type" :value="type">
                                {{ type }}
                            </option>
                        </select>
                    </div>
                    <button class="btn-pinko btn-secondary" @click="resetFilters">Reset Filters</button>
                </div>

                <!-- Giveaway Table -->
                <div class="wrapper px-3">
                    <ShimmerTable v-if="loading" />
                    <div v-else class="table-responsive">
                        <table class="giveaway-table">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Type</th>
                                    <th>Value</th>
                                    <th>Start Date</th>
                                    <th>End Date</th>
                                    <th>Entries</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="giveaway in giveaways" :key="giveaway.id">
                                    <td class="item-cell">
                                        <div class="item-info">
                                            <img :src="giveaway.item_detail?.image" :alt="giveaway.item_detail?.short_name">
                                            <span>{{ giveaway.item_detail?.name }}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span class="badge" :class="giveaway.type.toLowerCase()">
                                            {{ giveaway.type }}
                                        </span>
                                        <span class="badge" :class="giveaway.giveaway_type.toLowerCase()">
                                            {{ giveaway.giveaway_type }}
                                        </span>
                                    </td>
                                    <td>${{ (giveaway.value / 1000).toFixed(2) }}</td>
                                    <td>{{ formatDate(giveaway.starts_at) }}</td>
                                    <td>{{ formatDate(giveaway.expires_at) }}</td>
                                    <td>{{ giveaway.total_entries }}</td>
                                    <td>
                                        <span class="status-badge" :class="giveaway.status.toLowerCase()">
                                            {{ giveaway.status }}
                                        </span>
                                    </td>
                                    <td class="actions-cell">
                                        <button class="btn-pinko btn-sm btn-info" @click="editGiveaway(giveaway)">
                                            <i class="fas fa-edit"></i>
                                        </button>
                                        <button class="btn-pinko btn-sm btn-danger" @click="deleteGiveaway(giveaway.id)">
                                            <i class="fas fa-trash"></i>
                                        </button>
                                        <button class="btn-pinko btn-sm btn-success" @click="viewParticipants(giveaway)">
                                            <i class="fas fa-users"></i>
                                        </button>
                                        <button 
                                            v-if="giveaway.status === 'EXPIRED' && !giveaway.winner_id" 
                                            class="btn-pinko btn-sm btn-warning" 
                                            @click="selectWinner(giveaway)"
                                        >
                                            <i class="fas fa-trophy"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- Tasks Tab -->
            <div v-if="activeTab === 'tasks'">
                <div class="f sb flex-start pb-4">
                    <button class="btn-pinko btn-pink" @click="showTaskModal = true">
                        Create New Task
                    </button>
                </div>
                <!-- Task Creation Section -->
                <div class="task-section">
                    <h2>Create New Task</h2>
                    <div class="task-form">
                        <div class="form-group">
                            <label>Description</label>
                            <input 
                                type="text" 
                                v-model="newTask.description" 
                                placeholder="Enter task description"
                                class="form-input"
                            >
                        </div>
                        <div class="form-group">
                            <label>Icon</label>
                            <input 
                                type="text" 
                                v-model="newTask.icon" 
                                placeholder="Enter task type"
                                class="form-input"
                            >
                        </div>
                        <div class="form-group">
                            <label>Type</label>
                            <input 
                                type="text" 
                                v-model="newTask.type" 
                                placeholder="Enter task type"
                                class="form-input"
                            >
                        </div>
                        <button class="submit-btn" @click="createTask" :disabled="!isTaskFormValid">
                            Create Task
                        </button>
                    </div>
                </div>
                <!-- Task List -->
                <div class="task-list">
                    <div v-for="task in tasks" :key="task.id" class="task-item">
                        <div class="task-icon">
                            <i :class="task.icon"></i>
                        </div>
                        <div class="task-info">
                            <h5>{{ task.description }}</h5>
                            <span :class="['task-type', task.type]">{{ task.type }}</span>
                        </div>
                        <div class="task-actions">
                            <button class="action-btn edit" @click="editTask(task)">
                                <i class="fas fa-edit"></i>
                            </button>
                            <button class="action-btn delete" @click="deleteTask(task)">
                                <i class="fas fa-trash"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="showCreateModal" class="modal-overlay">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="text-white">{{ editingGiveaway ? 'Edit Giveaway' : 'Create New Giveaway' }}</h5>
                    <button class="close-btn" @click="closeModal">&times;</button>
                </div>
                <div class="modal-body">
                    <form @submit.prevent="saveGiveaway">
                        <div class="form-group">
                            <ListSearch ref="listSearch" @go-item="handleItemSelect" :data="suggestions"
                                @handleSearch="handleSearch" :holder="'Search an item...'" />
                        </div>
                        <div v-if="selectedItem" class="selected-item">
                            <div class="item-info">
                                <img :src="selectedItem.image" :alt="selectedItem.name" class="item-image">
                                <div class="item-details">
                                    <h5>{{ selectedItem.name }}</h5>
                                    <p>Price: ${{ selectedItem.price_info[0]?.price / 1000 }}</p>
                                </div>
                            </div>
                        </div>
                        <div class="form-group">
                            <label>Type</label>
                            <select v-model="giveawayForm.type" class="form-control" required>
                                <option v-for="type in types" :key="type" :value="type">
                                    {{ type }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>Giveaway Type</label>
                            <select v-model="giveawayForm.giveaway_type" class="form-control" required>
                                <option v-for="type in giveawayTypes" :key="type" :value="type">
                                    {{ type }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>Value (in cents)</label>
                            <input v-model.number="giveawayForm.value" type="decimal" class="form-control" required>
                        </div>
                        <div class="form-group">
                            <label>Start Date</label>
                            <input v-model="giveawayForm.starts_at" type="datetime-local" class="form-control" required>
                        </div>
                        <div class="form-group">
                            <label>End Date</label>
                            <input v-model="giveawayForm.expires_at" type="datetime-local" class="form-control"
                                required>
                        </div>
                        <div class="form-group">
                            <label>Status</label>
                            <select v-model="giveawayForm.status" class="form-control">
                                <option v-for="status in statuses" :key="status" :value="status">
                                    {{ status }}
                                </option>
                            </select>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn-pinko btn-secondary" @click="closeModal">Cancel</button>
                            <button type="submit" class="btn-pinko btn-pink">Save</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <div v-if="showParticipantsModal" class="modal-overlay">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="text-white">Participants</h5>
                    <button class="close-btn" @click="showParticipantsModal = false">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="entries-list">
                        <div v-for="entry in entries" :key="entry.id" class="entry-item">
                            <div class="user-info">
                                <img :src="entry.user.avatar_medium" :alt="entry.user.user_name" class="user-avatar">
                                <div class="user-details">
                                    <a :href="entry.user.profile_url" target="_blank" class="user-name">
                                        {{ entry.user.user_name }}
                                    </a>
                                    <span class="join-date">Joined: {{ formatDate(entry.joined_at) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Winner Selection Modal -->
        <div v-if="showWinnerModal" class="modal-overlay">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="text-white">Select Winner</h5>
                    <button class="close-btn" @click="showWinnerModal = false">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="giveaway-info mb-4">
                        <div class="item-info">
                            <img :src="selectedGiveaway?.item_detail?.image" :alt="selectedGiveaway?.item_detail?.name" class="item-image">
                            <div class="item-details">
                                <h5 class="text-white">{{ selectedGiveaway?.item_detail?.name }}</h5>
                                <p class="text-white">Total Entries: {{ selectedGiveaway?.total_entries }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="winner-selection">
                        <div v-if="!selectedWinner" class="text-center">
                            <button class="btn-pinko btn-pink btn-lg" @click="drawWinner" :disabled="isDrawing">
                                <i class="fas fa-random mr-2"></i>
                                {{ isDrawing ? 'Drawing...' : 'Draw Winner' }}
                            </button>
                        </div>

                        <div v-else class="winner-info">
                            <h6 class="text-center mb-3 text-white">Winner Selected!</h6>
                            <div class="winner-card">
                                <img :src="selectedWinner.avatar_medium" :alt="selectedWinner.user_name" class="winner-avatar">
                                <div class="winner-details">
                                    <h5 class="text-white">{{ selectedWinner.user_name }}</h5>
                                    <div class="winner-stats">
                                        <div class="stat-item">
                                            <span class="stat-label">Steam ID:</span>
                                            <span class="stat-value">{{ selectedWinner.steam_id }}</span>
                                        </div>
                                        <div class="stat-item">
                                            <span class="stat-label">Joined:</span>
                                            <span class="stat-value">{{ formatDate(selectedWinner.joined_at) }}</span>
                                        </div>
                                    </div>
                                    <div class="winner-actions mt-3">
                                        <a :href="selectedWinner.profile_url" target="_blank" class="steam-profile">
                                            <i class="fab fa-steam"></i> Steam Profile
                                        </a>
                                        <a v-if="selectedWinner.trade_link" :href="selectedWinner.trade_link" target="_blank" class="trade-link">
                                            <i class="fas fa-exchange-alt"></i> Trade Link
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <div class="winner-actions mt-4">
                                <button class="btn-pinko btn-secondary" @click="showWinnerModal = false">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="f align-center w100 justify-center mt-4">
            <PaginationRow :total-pages="pagination.totalPages" :page-number="pagination.currentPage"
                @update:pageNumber="pagination.pageNumber = $event" />
        </div>

    </div>
    <InfoModal
        v-if="showInfoModal"
        :message="infoMessage"
        @close="showInfoModal = false"
    />
</template>

<script>
import GiveawayService from "@/services/GiveawayService.js";
import ShimmerTable from "@/components/skeleton/ShimmerTable.vue";
import DateUtils from "@/utils/DateUtils.js";
import ListSearch from "@/components/shared/ListSearch.vue";
import ItemService from "@/services/ItemService.js";
import PaginationRow from "@/components/shared/PaginationRow.vue";
import InfoModal from "@/components/modal/InfoModal.vue";
import AdminTaskService from "@/services/admin.TaskService.js";
import AdminGiveawayService from "@/services/admin.GiveawayService.js";

export default {
    name: "AdminGiveawayView",
    components: {
        ShimmerTable,
        ListSearch,
        PaginationRow,
        InfoModal
    },
    data() {
        return {
            loading: true,
            suggestions: [],
            giveaways: [],
            entries: [],
            showCreateModal: false,
            showParticipantsModal: false,
            editingGiveaway: null,
            selectedGiveaway: null,
            filters: {
                type: "",
                status: "",
                giveaway_type: ""
            },
            types: ["Monthly", "Weekly", "Daily"],
            giveawayTypes: ["Daily", "Monthly", "Special"],
            statuses: ["ACTIVE", "EXPIRED", "CANCELLED"],
            giveawayForm: {
                type: null,
                value: 0,
                total_entries: 0,
                starts_at: "",
                expires_at: "",
                item_id: null,
                status: "ACTIVE",
                giveaway_type: null,
                winner_id: null,
                usersId: null
            },
            search: "",
            selectedItem: null,
            pagination: {
                currentPage: 1,
                totalPages: 1,
                totalItems: 0,
                itemsPerPage: 10
            },
            showWinnerModal: false,
            selectedWinner: null,
            isDrawing: false,
            showInfoModal: false,
            infoMessage: "",
            newTask: {
                description: '',
                icon: 'fas fa-star',
                type: 'daily'
            },
            availableIcons: [
                'fas fa-star',
                'fas fa-gift',
                'fas fa-trophy',
                'fas fa-medal',
                'fas fa-crown',
                'fas fa-gem',
                'fas fa-coins',
                'fas fa-box'
            ],
            activeTab: 'giveaways',
            tasks: []
        };
    },
    computed: {
        isTaskFormValid() {
            return this.newTask.description.trim() !== '' && 
                   this.newTask.icon && 
                   this.newTask.type;
        }
    },
    methods: {
        resetFilters() {
            this.filters = {
                type: "",
                status: "",
                giveaway_type: ""
            };
            this.getGiveaways();
        },
        async handleSearch(search) {
            this.search = search;
            await this.getItems();
        },
        async getItems() {
            const data = await ItemService.getAllItemImage({
                name: this.search,
                limit: 10,
                game: 730
            });
            this.suggestions = data;
        },
        async getEntries() {
            const response = await GiveawayService.getEntries({
                giveaway_id: this.selectedGiveaway.id
            });
            if (!response) return this.entries = [];
            this.entries = response;
        },
        handleItemSelect(item) {
            this.selectedItem = item;
            this.giveawayForm.item_id = item.general_id;
            this.giveawayForm.value = item?.price_info[0]?.price;
        },
        formatDate(date) {
            return DateUtils.getDate(date);
        },
        async getGiveaways() {
            this.loading = true;
            const response = await GiveawayService.getAllWithDetails({
                type: this.filters.type || undefined,
                status: this.filters.status || undefined,
                giveaway_type: this.filters.giveaway_type || undefined,
                page: this.pagination.currentPage,
                limit: this.pagination.itemsPerPage
            });

            if (!response.success) {
                this.giveaways = [];
                this.pagination.totalItems = 0;
                this.pagination.totalPages = 1;
            } else {
                this.giveaways = response.items;
                this.pagination.totalItems = response.totalCount || 0;
                this.pagination.totalPages = response.totalPages || 1;
            }
            this.loading = false;
        },
        editGiveaway(giveaway) {
            this.editingGiveaway = giveaway;
            this.giveawayForm = {
                type: giveaway.type,
                value: giveaway.value,
                total_entries: giveaway.total_entries,
                starts_at: giveaway.starts_at,
                expires_at: giveaway.expires_at,
                item_id: giveaway.item_id,
                status: giveaway.status,
                giveaway_type: giveaway.giveaway_type,
                winner_id: giveaway.winner_id,
                usersId: giveaway.usersId
            };
            this.showCreateModal = true;
        },
        async deleteGiveaway(id) {
            if (confirm("Are you sure you want to delete this giveaway?")) {
                const response = await AdminGiveawayService.deleteGiveaway(id);
                if (response) {
                    await this.getGiveaways();
                }
            }
        },
        viewParticipants(giveaway) {
            this.selectedGiveaway = giveaway;
            this.showParticipantsModal = true;
            this.getEntries()
        },
        closeModal() {
            this.showCreateModal = false;
            this.editingGiveaway = null;
            this.selectedItem = null;
            this.giveawayForm = {
                type: null,
                value: 0,
                total_entries: 0,
                starts_at: "",
                expires_at: "",
                item_id: null,
                status: "ACTIVE",
                giveaway_type: null,
                winner_id: null,
                usersId: null
            };
        },
        async saveGiveaway() {
            const response = this.editingGiveaway
                ? await AdminGiveawayService.updateGiveaway(this.editingGiveaway.id, this.giveawayForm)
                : await AdminGiveawayService.create(this.giveawayForm);

            if (response) {
                await this.getGiveaways();
                //this.closeModal();
            }
        },
        async handlePageChange(page) {
            this.pagination.currentPage = page;
            console.log(page);
            await this.getGiveaways();
        },
        async selectWinner(giveaway) {
            this.selectedGiveaway = giveaway;
            this.showWinnerModal = true;
            this.selectedWinner = null;
        },
        async drawWinner() {
            this.isDrawing = true;
            try {
                const response = await AdminGiveawayService.selectWinner({ 
                    giveaway_id: this.selectedGiveaway.id 
                });

                if (!response.success) {
                    this.showInfo(response.message);
                    return;
                }

                this.selectedWinner = response.data;
                this.showInfo(response.message);
            } catch (error) {
                console.error('Error drawing winner:', error);
                this.showInfo('Error occurred while selecting winner');
            } finally {
                this.isDrawing = false;
            }
        },
        resetWinnerSelection() {
            this.selectedWinner = null;
        },
        showInfo(message) {
            this.infoMessage = message;
            this.showInfoModal = true;
            setTimeout(() => {
                this.showInfoModal = false;
            }, 3000);
        },
        async createTask() {
            try {
                if (!this.isTaskFormValid) return;
                
                const response = await AdminTaskService.createTask(this.newTask);
                if (response) {
                    this.infoMessage = "Task created successfully";
                    this.showInfoModal = true;
                    this.showTaskModal = false;
                    this.fetchTasks();
                }
            } catch (error) {
                console.error('Error creating task:', error);
                this.infoMessage = "Failed to create task";
                this.showInfoModal = true;
            }
        },
        async fetchTasks() {
            try {
                const response = await AdminTaskService.getAll();
                if (!response) {
                    this.tasks = [];
                }

                this.tasks = response.tasks;
            } catch (error) {
                console.error('Error fetching tasks:', error);
                this.infoMessage = "Failed to fetch tasks";
                this.showInfoModal = true;
            }
        },
        async deleteTask(task) {
            try {
                const response = await AdminTaskService.deleteTask(task.id);
                if (response) {
                    this.infoMessage = "Task deleted successfully";
                    this.showInfoModal = true;
                    this.fetchTasks();
                }
            } catch (error) {
                console.error('Error deleting task:', error);
                this.infoMessage = "Failed to delete task";
                this.showInfoModal = true;
            }
        },
        editTask(task) {
            this.newTask = { ...task };
            this.showTaskModal = true;
        },
        async updateTask() {
            try {
                const response = await AdminTaskService.update(this.newTask.id, this.newTask);
                if (response) {
                    this.infoMessage = "Task updated successfully";
                    this.showInfoModal = true;
                    this.showTaskModal = false;
                    this.fetchTasks();
                }
            } catch (error) {
                console.error('Error updating task:', error);
                this.infoMessage = "Failed to update task";
                this.showInfoModal = true;
            }
        }
    },
    watch: {
        'filters.type'() {
            this.getGiveaways();
        },
        'filters.status'() {
            this.getGiveaways();
        },
        'filters.giveaway_type'() {
            this.getGiveaways();
        },
        activeTab(newTab) {
            if (newTab === 'tasks') {
                this.fetchTasks();
            }
        }
    },
    async created() {
        await this.getGiveaways();
    }
};
</script>

<style scoped>
.table-responsive {
    overflow-x: auto;
    background: var(--secondary-color);
    border-radius: 8px;
    padding: 1px;
}

.giveaway-table {
    width: 100%;
    border-collapse: collapse;
    color: white;
}

.giveaway-table th,
.giveaway-table td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.giveaway-table th {
    background: rgba(255, 255, 255, 0.05);
    font-weight: 600;
    white-space: nowrap;
}

.giveaway-table tbody tr:hover {
    background: rgba(255, 255, 255, 0.05);
}

.item-cell {
    min-width: 250px;
}

.item-info {
    display: flex;
    align-items: center;
    gap: 10px;
}

.item-info img {
    width: 40px;
    height: 30px;
    object-fit: contain;
}

.badge {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    margin-right: 5px;
}

.badge.monthly {
    background-color: #007bff;
    color: white;
}

.badge.weekly {
    background-color: #28a745;
    color: white;
}

.badge.daily {
    background-color: #ffc107;
    color: black;
}

.badge.special {
    background-color: #dc3545;
    color: white;
}

.badge.regular {
    background-color: #6c757d;
    color: white;
}

.status-badge {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
}

.status-badge.active {
    background-color: #28a745;
    color: white;
}

.status-badge.ended {
    background-color: #dc3545;
    color: white;
}

.status-badge.draft {
    background-color: #ffc107;
    color: black;
}

.actions-cell {
    white-space: nowrap;
}

.actions-cell .btn-pinko {
    padding: 4px 8px;
    margin: 0 2px;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.85);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2;
    backdrop-filter: blur(8px);
}

.modal-content {
    background: var(--secondary-color);
    border-radius: 12px;
    width: 90%;
    max-width: 600px;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
    from {
        opacity: 0;
        transform: translateY(-20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.modal-header {
    padding: 15px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 12px 12px 0 0;
}

.modal-body {
    padding: 20px;
}

.modal-footer {
    padding: 15px 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.form-group {
    margin-bottom: 15px;
}

.form-group label {
    display: block;
    margin-bottom: 5px;
    color: white;
}

.form-control {
    width: 100%;
    padding: 8px;
    border-radius: 4px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.05);
    color: white;
}

.form-control:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.2);
}

.total-entries {
    text-align: center;
    padding: 20px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
    font-size: 18px;
    font-weight: bold;
}

.no-participants {
    text-align: center;
    color: rgba(255, 255, 255, 0.5);
    padding: 20px;
}

.close-btn {
    background: none;
    border: none;
    color: white;
    font-size: 24px;
    cursor: pointer;
    transition: all 0.2s ease;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
}

.close-btn:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: rotate(90deg);
}

.btn-pinko {
    padding: 8px 16px;
    border-radius: 4px;
    border: none;
    cursor: pointer;
    font-weight: 500;
    width: auto;
}

.btn-sm {
    padding: 4px 8px;
    font-size: 14px;
}

.btn-pink {
    background-color: #8f24c0;
    color: white;
}

.btn-secondary {
    background-color: #9b26d2;
    color: white;
}

.btn-danger {
    background-color: #dc3545;
    color: white;
}

.btn-info {
    background-color: #17a2b8;
    color: white;
}

.btn-success {
    background-color: #28a745;
    color: white;
}

.filters-container {
    display: flex;
    gap: 15px;
    align-items: flex-end;
    flex-wrap: wrap;
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 150px;
}

.filter-group label {
    color: white;
    font-size: 14px;
}

.selected-item {
    margin: 10px 0;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: #f8f9fa;
}

.selected-item .item-info {
    display: flex;
    align-items: center;
    gap: 10px;
}

.selected-item .item-image {
    width: 50px;
    height: 50px;
    object-fit: contain;
}

.selected-item .item-details {
    flex: 1;
}

.selected-item h5 {
    margin: 0;
}

.selected-item p {
    margin: 5px 0 0;
    color: #666;
}

.entries-list {
    max-height: 400px;
    overflow-y: auto;
    padding: 4px;
}

.entries-list::-webkit-scrollbar {
    width: 6px;
}

.entries-list::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
}

.entries-list::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
}

.entries-list::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.2);
}

.entry-item {
    padding: 12px;
    margin: 8px 0;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    transition: all 0.2s ease;
    border: 1px solid rgba(255, 255, 255, 0.05);
}

.entry-item:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    border-color: rgba(255, 255, 255, 0.1);
}

.user-info {
    display: flex;
    align-items: center;
    gap: 10px;
}

.user-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
}

.entry-item:hover .user-avatar {
    border-color: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
}

.user-details {
    display: flex;
    flex-direction: column;
}

.user-name {
    color: #007bff;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.2s ease;
}

.user-name:hover {
    color: #0056b3;
    text-shadow: 0 0 8px rgba(0, 123, 255, 0.3);
}

.join-date {
    font-size: 0.8em;
    color: white;
}

.winner-selection {
    padding: 2rem 0;
}

.winner-card {
    background: linear-gradient(145deg, #222, #2a2a2a);
    border-radius: 12px;
    padding: 1.5rem;
    display: flex;
    align-items: flex-start;
    gap: 1.5rem;
    border: 1px solid #333;
}

.winner-avatar {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    border: 3px solid #ff4655;
}

.winner-details {
    flex: 1;
}

.winner-details h5 {
    font-size: 1.25rem;
    margin: 0;
}

.winner-stats {
    margin: 1rem 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.stat-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.stat-label {
    color: #999;
    font-size: 0.875rem;
}

.stat-value {
    color: #fff;
    font-size: 0.875rem;
    font-weight: 500;
}

.winner-actions {
    display: flex;
    gap: 1rem;
}

.steam-profile, .trade-link {
    color: #fff;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    font-size: 0.9rem;
    transition: all 0.2s ease;
}

.steam-profile:hover, .trade-link:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
}

.steam-profile i, .trade-link i {
    font-size: 1rem;
}

.trade-link {
    background: rgba(255, 70, 85, 0.2);
}

.trade-link:hover {
    background: rgba(255, 70, 85, 0.3);
}

.task-section {
    background: #1a1a1a;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
}

.task-section h2 {
    color: #fff;
    margin-bottom: 20px;
    font-size: 1.5rem;
}

.task-form {
    display: grid;
    gap: 20px;
    max-width: 600px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.form-group label {
    color: #fff;
    font-size: 0.9rem;
}

.form-input {
    background: #2a2a2a;
    border: 1px solid #3a3a3a;
    border-radius: 8px;
    padding: 12px;
    color: #fff;
    font-size: 1rem;
    transition: all 0.3s ease;
}

.form-input:focus {
    border-color: #4a4a4a;
    outline: none;
}

.icon-selector {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
    gap: 12px;
    padding: 12px;
    background: #2a2a2a;
    border-radius: 8px;
}

.icon-option {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    background: #3a3a3a;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.icon-option i {
    font-size: 1.5rem;
    color: #fff;
}

.icon-option:hover {
    background: #4a4a4a;
}

.icon-option.selected {
    background: #4a4a4a;
    border: 2px solid #5a5a5a;
}

.submit-btn {
    background: #4a4a4a;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 12px 24px;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
}

.submit-btn:hover:not(:disabled) {
    background: #5a5a5a;
}

.submit-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

@media (max-width: 768px) {
    .task-form {
        grid-template-columns: 1fr;
    }
    
    .icon-selector {
        grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
    }
    
    .icon-option {
        width: 40px;
        height: 40px;
    }
}

.tab-nav {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
    border-bottom: 1px solid #2a2a2a;
    padding-bottom: 16px;
}

.tab-btn {
    padding: 8px 16px;
    background: transparent;
    border: none;
    color: #fff;
    cursor: pointer;
    transition: all 0.3s ease;
}

.tab-btn.active {
    color: #ff4655;
    border-bottom: 2px solid #ff4655;
}

.task-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.task-item {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    background: #1a1a1a;
    border-radius: 8px;
}

.task-icon {
    width: 40px;
    height: 40px;
    background: #2a2a2a;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.task-icon i {
    font-size: 1.2rem;
    color: #ff4655;
}

.task-info {
    flex: 1;
}

.task-info h5 {
    color: #fff;
    margin: 0 0 4px 0;
}

.task-type {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.8rem;
    text-transform: capitalize;
}

.task-type.daily {
    background: rgba(5, 150, 105, 0.2);
    color: #059669;
}

.task-type.weekly {
    background: rgba(37, 99, 235, 0.2);
    color: #2563eb;
}

.task-type.monthly {
    background: rgba(124, 58, 237, 0.2);
    color: #7c3aed;
}

.task-type.special {
    background: rgba(245, 158, 11, 0.2);
    color: #f59e0b;
}

.task-actions {
    display: flex;
    gap: 8px;
}

.action-btn {
    padding: 8px;
    background: transparent;
    border: none;
    color: #fff;
    cursor: pointer;
    transition: all 0.3s ease;
}

.action-btn:hover {
    color: #ff4655;
}

.action-btn.delete:hover {
    color: #dc2626;
}
</style>
